// Bucket-anchored hover state hook for category-axis charts.
//
// Generalizes the SVG → CSS pixel coord conversion that ManaCurveChart needs
// for absolutely-positioning a tooltip outside the SVG. The hook owns:
//   - the hover state (bucket index + anchor in CSS pixels)
//   - a ref for the chart's positioned wrapper
//   - the toCss(svgX, svgY) helper that uses getBoundingClientRect
//
// Usage:
//   const { containerRef, hover, setHoverBucket, clearHover, toCss } =
//     useHoverBucket({ viewBoxW: 320, viewBoxH: 96 });
//
//   <div ref={containerRef} className="relative">
//     <svg viewBox="0 0 320 96" onMouseLeave={clearHover}>
//       <rect ... onMouseEnter={() => {
//         const css = toCss(cx, anchorY);
//         setHoverBucket(bucket, css);
//       }} />
//     </svg>
//   </div>

import { useCallback, useRef, useState } from "react";

export interface HoverBucketState {
  /** Discrete bucket index (CMC bucket, turn index, etc). */
  bucket: number;
  /** CSS-pixel x relative to the container's bounding rect. */
  cx: number;
  /** CSS-pixel y relative to the container's bounding rect. */
  cy: number;
}

export interface UseHoverBucketOptions {
  /** Width of the SVG viewBox. */
  viewBoxW: number;
  /** Height of the SVG viewBox. */
  viewBoxH: number;
}

export function useHoverBucket({ viewBoxW, viewBoxH }: UseHoverBucketOptions) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState<HoverBucketState | null>(null);

  /** SVG (viewBox) coords → CSS pixels relative to the container. Returns
   *  { x: 0, y: 0 } if the container hasn't mounted yet (rare, but possible
   *  if a parent suspends mid-render). */
  const toCss = useCallback(
    (svgX: number, svgY: number): { x: number; y: number } => {
      const el = containerRef.current;
      if (!el) return { x: 0, y: 0 };
      const rect = el.getBoundingClientRect();
      return {
        x: (svgX / viewBoxW) * rect.width,
        y: (svgY / viewBoxH) * rect.height,
      };
    },
    [viewBoxW, viewBoxH],
  );

  const setHoverBucket = useCallback(
    (bucket: number, css: { x: number; y: number }) => {
      setHover({ bucket, cx: css.x, cy: css.y });
    },
    [],
  );

  const clearHover = useCallback(() => setHover(null), []);

  return { containerRef, hover, setHoverBucket, clearHover, toCss };
}

export default useHoverBucket;
